import { useContext, useEffect, useRef, useState } from 'react';
import ImageService from '../../services/ImageService';
import MessageContext from '../../App/Contexts/MessageContext';

// An upload's tile stands in the Image Q from the moment the file is picked
// until the slides it became have downloaded:
// - uploading:  the bytes are going out (determinate bar)
// - processing: every byte is sent, but the request does not return until the
//               server has stored the file and, for a PDF or Office document,
//               converted it into slides
// - adding:     the server is done and said which slides it created; they are
//               still being listed and downloaded, which on a slow connection
//               is a noticeable wait of its own
// - failed:     with Retry and dismiss
export const UPLOADING = 'uploading';
export const PROCESSING = 'processing';
export const ADDING = 'adding';
export const FAILED = 'failed';

const percentOf = ({ loaded, total }, fallbackTotal) => {
    const size = total || fallbackTotal;
    if (!size) return 0;
    return Math.min(100, Math.round((loaded * 100) / size));
};

// What a tile shows until the real slide arrives: the picked image itself, or
// an icon for a document that the server turns into slides.
export const kindOf = (file) => {
    const name = (file.name || '').toLowerCase();
    const type = file.type || '';
    if (type.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
    if (/\.(docx?)$/.test(name) || type.includes('word')) return 'word';
    if (/\.(pptx?)$/.test(name) || type.includes('presentation') || type.includes('powerpoint')) return 'slides';
    if (/\.(xlsx?)$/.test(name) || type.includes('sheet') || type.includes('excel')) return 'sheet';
    // Browsers can't draw TIFF, so it gets an icon like the documents.
    if (type.startsWith('image/') && type !== 'image/tiff') return 'image';
    return 'file';
};

/**
 * Tracks the files this user is sending to the room's Image Q.
 *
 * Lives in the room rather than the drawer: on mobile the drawer unmounts when
 * the user switches tabs, and an upload's tile has to survive that.
 *
 * `slides` and `loadingSlides` are useSlides' downloaded slides and the ones
 * still downloading, so an upload knows when the slides it created have
 * arrived and its tile can make way for them.
 *
 * Returns `uploads`, one entry per file still in flight, adding, or failed:
 * `{ id, name, kind, previewUrl, index, status, progress, error, added, ready }`.
 */
export default function useFileUploads({ roomId, maxIndex, onUpload, slides = [], loadingSlides = [] }) {
    const { setMessage } = useContext(MessageContext);
    const [uploads, setUploads] = useState([]);
    const files = useRef(new Map()); // id -> File, kept for a retry
    const previews = useRef(new Map()); // id -> blob URL of the picked image
    // id -> itemNames of its slides the room has listed at least once. A slide
    // that was listed and then isn't any more was hidden or deleted, so the
    // tile must stop waiting for it to download.
    const seen = useRef(new Map());
    const nextId = useRef(1);

    // Indexes are handed out from here rather than straight from maxIndex, so
    // a second batch or a retry started before the slide list has refreshed
    // does not ask for an index the first one already took. The server never
    // reuses an index, so the gap a failed upload leaves behind is harmless.
    const lastIndex = useRef(maxIndex);
    useEffect(() => {
        if (maxIndex > lastIndex.current) lastIndex.current = maxIndex;
    }, [maxIndex]);
    const allocateIndex = () => {
        lastIndex.current = Math.max(lastIndex.current, maxIndex) + 1;
        return lastIndex.current;
    };

    useEffect(
        () => () => {
            previews.current.forEach((url) => window.URL.revokeObjectURL(url));
        },
        []
    );

    const patch = (id, changes) =>
        setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...changes } : u)));
    const remove = (id) => {
        files.current.delete(id);
        seen.current.delete(id);
        const preview = previews.current.get(id);
        if (preview) {
            window.URL.revokeObjectURL(preview);
            previews.current.delete(id);
        }
        setUploads((list) => list.filter((u) => u.id !== id));
    };

    // Hand each adding tile over to its slides once they have all downloaded.
    useEffect(() => {
        const downloaded = new Set(slides.map((s) => s.itemName));
        const listed = new Set(loadingSlides.map((s) => s.itemName));
        uploads
            .filter(({ status }) => status === ADDING)
            .forEach(({ id, added, ready }) => {
                const seenNames = seen.current.get(id) || new Set();
                added.forEach(({ itemName }) => {
                    if (downloaded.has(itemName) || listed.has(itemName)) seenNames.add(itemName);
                });
                seen.current.set(id, seenNames);
                const arrived = added.filter(({ itemName }) => downloaded.has(itemName)).length;
                const done = added.every(
                    ({ itemName }) =>
                        downloaded.has(itemName) || (seenNames.has(itemName) && !listed.has(itemName))
                );
                if (done) remove(id);
                else if (arrived !== ready) patch(id, { ready: arrived });
            });
    }, [slides, loadingSlides, uploads]);

    // Resolves true or false rather than rejecting, so a batch can tell which
    // of its files made it.
    const send = (id) => {
        const file = files.current.get(id);
        const formData = new FormData();
        formData.append('file', file);
        const index = allocateIndex();
        patch(id, { index });
        return ImageService.uploadImage(roomId, index, formData, {
            onUploadProgress: (event) => {
                const progress = percentOf(event, file.size);
                patch(id, progress >= 100 ? { progress: 100, status: PROCESSING } : { progress });
            }
        }).then(
            (response) => {
                const added = Array.isArray(response?.data) ? response.data : [];
                // Without the list there's nothing to wait for; the slide shows
                // up the way it did before tiles stayed around.
                // The server may have put a document somewhere other than the
                // index asked for (it was taken meanwhile); the tile moves there.
                if (added.length) {
                    patch(id, { status: ADDING, progress: 100, added, ready: 0, index: added[0].index });
                }
                else remove(id);
                return { ok: true };
            },
            (e) => {
                const error = e?.message || 'Failed to upload file';
                patch(id, { status: FAILED, error });
                return { ok: false, error };
            }
        );
    };

    const report = (results) => {
        const failures = results.filter(({ ok }) => !ok);
        if (!failures.length) {
            setMessage(results.length > 1 ? `${results.length} files uploaded` : 'File uploaded');
        } else if (results.length > 1) {
            setMessage(`${failures.length} of ${results.length} uploads failed`);
        } else {
            // The reason is on the tile as well, but the tile is easy to miss
            // when the Image Q isn't the tab in view.
            setMessage(`Upload failed: ${failures[0].error}`);
        }
        // Don't rely solely on the async PRESENTATION_CHANGE event to reveal the new
        // slide - refresh once here so the uploader's list updates deterministically.
        if (onUpload && results.some(({ ok }) => ok)) {
            onUpload();
        }
    };

    const start = (fileList) => {
        const entries = [...fileList].map((file) => {
            const id = nextId.current++;
            files.current.set(id, file);
            const kind = kindOf(file);
            let previewUrl = null;
            if (kind === 'image') {
                previewUrl = window.URL.createObjectURL(file);
                previews.current.set(id, previewUrl);
            }
            return {
                id,
                name: file.name,
                kind,
                previewUrl,
                index: null,
                status: UPLOADING,
                progress: 0,
                error: null,
                added: [],
                ready: 0
            };
        });
        if (!entries.length) return Promise.resolve([]);
        setUploads((list) => [...list, ...entries]);
        return Promise.all(entries.map(({ id }) => send(id))).then((results) => {
            report(results);
            return results.map(({ ok }) => ok);
        });
    };

    const retry = (id) => {
        if (!files.current.has(id)) return Promise.resolve(false);
        patch(id, { status: UPLOADING, progress: 0, error: null });
        return send(id).then((result) => {
            report([result]);
            return result.ok;
        });
    };

    return { uploads, start, retry, dismiss: remove };
}
